import type {
    HistorySourceTypeConfig,
    ResourceTypeConfig,
    SourceTypeTemplate,
} from "./history-source-config";
import { ResourceType } from "@common/types/auditLog";
import { APIProcessName, SQSProcessName } from "../process-names";
import { HistoryField, HistoryGroupBy } from "./history-fields";

export const TRANSACTION_FIELDS = [
    HistoryField.CATEGORY_ID,
    HistoryField.ENTITY_ID,
    HistoryField.COUNTERPARTY_ID,
    HistoryField.DATE,
    HistoryField.DESCRIPTION,
    HistoryField.NOTE,
    HistoryField.PAIRED_TRANSFER_ID,
    HistoryField.SPLIT_PARENT_ID,
    HistoryField.ACCOUNT_ID,
    HistoryField.ACCRUAL_ACCOUNT_ID,
    HistoryField.GENERAL_LEDGER_ACCOUNT_ID,
    HistoryField.CLASS_ASSIGNMENTS,
    HistoryField.EXCLUDE_FROM_1099,
    HistoryField.EXCLUDE_FROM_CASH_FLOW_REPORT,
];

const MANUAL_TRANSACTION_UPDATE_FIELDS = [
    HistoryField.AMOUNT,
    HistoryField.DATE_ONLY,
    HistoryField.BANK_DESCRIPTION,
];

export const TRANSACTION_SOURCE_TYPE_TEMPLATES: Record<
    string,
    SourceTypeTemplate
> = {
    [APIProcessName.TRANSACTION_BULK_UPDATE]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "through a bulk update",
    },
    [APIProcessName.TRANSACTION_UPDATE]: {
        verb: "Updated",
        noun: "transaction",
    },
    [APIProcessName.CREATE_MANUAL_TRANSACTIONS]: {
        verb: "Created",
        noun: "transaction",
    },
    [APIProcessName.UPDATE_MANUAL_TRANSACTION]: {
        verb: "Updated",
        noun: "transaction",
    },
    [APIProcessName.TRANSACTION_FINALIZE_DELETION]: {
        verb: "Deleted",
        noun: "transaction",
    },
    [APIProcessName.TRANSACTION_MATCH_TRANSFER]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "with transfer matching",
        fieldOverrides: {
            [HistoryField.PAIRED_TRANSFER_ID]: {
                verb: "Matched",
                noun: "transaction",
                skipFieldLabel: true,
            },
        },
    },
    [APIProcessName.TRANSACTION_UNMATCH_TRANSFER]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "with transfer matching",
        fieldOverrides: {
            [HistoryField.PAIRED_TRANSFER_ID]: {
                verb: "Unmatched",
                noun: "transaction",
                skipFieldLabel: true,
            },
        },
    },
    [SQSProcessName.TRANSACTION_TRANSFER_MATCHING]: {
        verb: "Updated",
        noun: "transaction",
        suffix: "with transfer matching",
        fieldOverrides: {
            [HistoryField.PAIRED_TRANSFER_ID]: {
                verb: "Matched",
                noun: "transaction",
                skipFieldLabel: true,
            },
        },
    },
    [APIProcessName.TRANSACTION_SPLIT_V2_UPDATE]: {
        verb: "Updated split",
        noun: "transaction",
        skipFieldLabel: true,
    },
    [APIProcessName.TRANSACTION_BULK_UNSPLIT]: {
        verb: "Unsplit",
        noun: "transaction",
        skipFieldLabel: true,
    },
};

const ACCRUAL_APPROVE_RESOURCE_TYPE_CONFIGS: ResourceTypeConfig[] = [
    {
        resourceType: ResourceType.TRANSACTION,
        fields: [
            HistoryField.ACCRUAL_ACCOUNT_ID,
            HistoryField.CLASS_ASSIGNMENTS,
        ],
    },
    {
        resourceType: ResourceType.INVOICE,
        fields: [
            HistoryField.ACCRUAL_ACCOUNT_ID,
            HistoryField.ACCOUNTS_RECEIVABLE_ID,
            HistoryField.CLASS_ASSIGNMENTS,
        ],
    },
    {
        resourceType: ResourceType.BILL,
        fields: [
            HistoryField.ACCRUAL_ACCOUNT_ID,
            HistoryField.ACCOUNTS_PAYABLE_ID,
            HistoryField.CLASS_ASSIGNMENTS,
        ],
    },
];

export const transactionSources: HistorySourceTypeConfig[] = [
    {
        sourceType: APIProcessName.TRANSACTION_BULK_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: [APIProcessName.PLATFORM_UPDATE_TRANSACTION],
        enabled: true,
    },
    {
        sourceType: APIProcessName.CREATE_MANUAL_TRANSACTIONS,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.UPDATE_MANUAL_TRANSACTION,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: MANUAL_TRANSACTION_UPDATE_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_FINALIZE_DELETION,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_MATCH_TRANSFER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_UNMATCH_TRANSFER,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: SQSProcessName.TRANSACTION_TRANSFER_MATCHING,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: SQSProcessName.TRANSACTION_RULE_APPLICATION,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_SPLIT_V2_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: [HistoryField.SPLIT_PARENT_ID],
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_BULK_UNSPLIT,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: [HistoryField.SPLIT_PARENT_ID],
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: SQSProcessName.AUDIT_LOG_ROLLBACK,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_REVIEW_APPROVE,
        resourceTypeConfigs: ACCRUAL_APPROVE_RESOURCE_TYPE_CONFIGS,
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_REVIEW_BULK_APPROVE,
        resourceTypeConfigs: ACCRUAL_APPROVE_RESOURCE_TYPE_CONFIGS,
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_REVIEW_UNDO,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: [HistoryField.ACCRUAL_ACCOUNT_ID],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_REVIEW_BULK_UNMATCH_INVOICES,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: [HistoryField.ACCRUAL_ACCOUNT_ID],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_MATCH_CREATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ACCRUAL_MATCH,
                fields: [HistoryField.ACCRUAL_MATCHES],
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.ACCRUAL_MATCH_REMOVE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.ACCRUAL_MATCH,
                fields: [HistoryField.ACCRUAL_MATCHES],
                resolveEntity: true,
            },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_FIELD_REVIEW_ADD,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_FIELD_REVIEW_REVOKE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_FIELD_REVIEW_BULK_ADD,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.TRANSACTION_FIELD_REVIEW_BULK_REVOKE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION_FIELD_REVIEW,
                fields: [HistoryField.REVIEWED_FIELD],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
];
